// Deterministic replays of the inbox's guards, shapes and tests for Lesson 4.2 ("Deciding What
// the Model May Decide"). The proposals and titles below come from the runs recorded on
// 8 October 2026 for Lesson 4.1. The graphics replay those recordings and never call a model.

export type Tone = 'neutral' | 'success' | 'warning';
export interface Summary {
  message: string;
  tone: Tone;
}
export const yesNo = (value: boolean) => (value ? 'yes' : 'no');
export const TITLE_LIMIT = 80;
export const LONG_TITLE =
  'Order ten more name badges for the partners before the 10th so that everyone registered has one';

// --- decide-table: rule, step or judgment ----------------------------------------------------

export const DECISIONS = [
  'membership',
  'question',
  'mentions-task',
  'title-length',
  'next-step',
  'duplicate',
] as const;
export type Decision = (typeof DECISIONS)[number];
export const decisionLabels: Record<Decision, string> = {
  membership: 'May Sam be assigned?',
  question: 'Is this message a question?',
  'mentions-task': 'Does the note mention a task?',
  'title-length': 'Is the title under 80 characters?',
  'next-step': 'Which step runs next?',
  duplicate: 'Is the proposal a duplicate?',
};
export const toDecision = (value: string): Decision =>
  DECISIONS.find((decision) => decision === value) ?? 'membership';
export type Kind = 'rule' | 'step' | 'judgment';
export const deciders: Record<Kind, string> = {
  rule: 'code',
  step: 'the author',
  judgment: 'the model',
};
export const decisionKinds: Record<Decision, Kind> = {
  membership: 'rule',
  question: 'judgment',
  'mentions-task': 'judgment',
  'title-length': 'rule',
  'next-step': 'step',
  duplicate: 'rule',
};
export const decisionReasons: Record<Decision, string> = {
  membership: 'Membership is a row in the project table, so code looks it up.',
  question: 'Whether words ask something is meaning, so the router reads it.',
  'mentions-task': 'What the note means is meaning, so the extraction reads it.',
  'title-length': 'A length is counted, never judged.',
  'next-step': 'The author wrote the switch on the label, so the path is already chosen.',
  duplicate: 'An exact title match is a comparison code makes.',
};
const ALREADY_MODEL = "This is already the model's decision, checked by code afterwards.";
/** What happens when the model is asked to make the decision instead. */
export const modelConsequences: Record<Decision, string> = {
  membership: 'The model may say yes to Sam, and a permission leaks.',
  question: ALREADY_MODEL,
  'mentions-task': ALREADY_MODEL,
  'title-length': 'The model may miscount, and a long title gets written.',
  'next-step':
    'The model may pick a step the author never wrote, so the log cannot explain the run.',
  duplicate: 'The model cannot see the task list unless we send it, so a duplicate slips through.',
};
export interface TableState {
  decision: Decision;
  revealed: boolean;
  modelDecides: boolean;
}
export const initialTable = (): TableState => ({
  decision: 'membership',
  revealed: false,
  modelDecides: false,
});
export type TableAction =
  | { type: 'decision'; value: Decision }
  | { type: 'reveal' }
  | { type: 'toggle-model' }
  | { type: 'reset' };
export function tableTransition(state: TableState, action: TableAction): TableState {
  switch (action.type) {
    case 'reset':
      return initialTable();
    case 'decision':
      return { ...state, decision: action.value, revealed: false };
    case 'reveal':
      return { ...state, revealed: true };
    case 'toggle-model':
      return { ...state, modelDecides: !state.modelDecides };
  }
}
export const TABLE_START_MESSAGE = 'Choose a decision, then press Reveal.';
export const TABLE_REFINEMENT =
  'Refinement: rules in code, judgments to the model, steps chosen by the author.';
export function tableSummary(state: TableState): Summary {
  if (!state.revealed) return { tone: 'neutral', message: TABLE_START_MESSAGE };
  const kind = decisionKinds[state.decision];
  if (state.modelDecides)
    return {
      tone: kind === 'judgment' ? 'success' : 'warning',
      message: modelConsequences[state.decision],
    };
  return { tone: 'neutral', message: decisionReasons[state.decision] };
}

// --- decide-guard: input guard, model, output guard, write -----------------------------------

export const GUARD_PROPOSALS = ['sam', 'long', 'ravi', 'task-999'] as const;
export type GuardProposal = (typeof GUARD_PROPOSALS)[number];
export const guardProposalLabels: Record<GuardProposal, string> = {
  sam: 'Assign the badges to Sam',
  long: `A ${LONG_TITLE.length}-character title`,
  ravi: 'Assign the badges to Ravi',
  'task-999': 'Assign task 999 to Ravi',
};
export const toGuardProposal = (value: string): GuardProposal =>
  GUARD_PROPOSALS.find((proposal) => proposal === value) ?? 'sam';
/** The proposal the model returned, as the output guard reads it. */
export const guardProposalJson: Record<GuardProposal, string> = {
  sam: '{"task_id": 5, "assignee": "Sam"}',
  long: `{"title": "${LONG_TITLE}", "assignee": null}`,
  ravi: '{"task_id": 5, "assignee": "Ravi"}',
  'task-999': '{"task_id": 999, "assignee": "Ravi"}',
};
export const GUARD_STAGES = ['Input guard', 'Model', 'Output guard', 'Write'] as const;
export type Outcome = 'passed' | 'stopped' | 'not reached';
export interface GuardStage {
  stage: (typeof GUARD_STAGES)[number];
  outcome: Outcome;
  detail: string;
}
export interface GuardRun {
  stages: GuardStage[];
  /** The index of the stage that stopped the proposal, or null when it was written. */
  stoppedAt: number | null;
  reason: string;
  written: boolean;
}
/** The four stages before anything is sent: nothing reached yet. */
export const waitingStages = (): GuardStage[] =>
  GUARD_STAGES.map((stage): GuardStage => ({ stage, outcome: 'not reached', detail: '' }));
export const INPUT_GUARD_DETAIL = 'message length ok, Maya is a member, budget left';
export const MODEL_DETAIL = 'returned the proposal';
export const outputGuardReasons: Record<GuardProposal, string> = {
  sam: 'Sam is not a member of Apollo',
  long: `schema: the title is ${LONG_TITLE.length} characters, the limit is ${TITLE_LIMIT}`,
  ravi: 'schema ok, task 5 exists, Ravi is a member',
  'task-999': 'no task 999 in Apollo',
};
export function runGuards(proposal: GuardProposal): GuardRun {
  const stopped = proposal !== 'ravi';
  const stages: GuardStage[] = [
    { stage: 'Input guard', outcome: 'passed', detail: INPUT_GUARD_DETAIL },
    { stage: 'Model', outcome: 'passed', detail: MODEL_DETAIL },
    {
      stage: 'Output guard',
      outcome: stopped ? 'stopped' : 'passed',
      detail: outputGuardReasons[proposal],
    },
    {
      stage: 'Write',
      outcome: stopped ? 'not reached' : 'passed',
      detail: stopped ? 'nothing written' : 'task 5 assigned to Ravi',
    },
  ];
  return {
    stages,
    stoppedAt: stopped ? 2 : null,
    reason: outputGuardReasons[proposal],
    written: !stopped,
  };
}
export interface GuardState {
  proposal: GuardProposal;
  run: GuardRun | null;
}
export const initialGuard = (): GuardState => ({ proposal: 'sam', run: null });
export type GuardAction =
  { type: 'proposal'; value: GuardProposal } | { type: 'send' } | { type: 'reset' };
export function guardTransition(state: GuardState, action: GuardAction): GuardState {
  switch (action.type) {
    case 'reset':
      return initialGuard();
    case 'proposal':
      return { proposal: action.value, run: null };
    case 'send':
      return { ...state, run: runGuards(state.proposal) };
  }
}
export const GUARD_START_MESSAGE = 'Choose a proposal, then press Send through the guards.';
export const guardMessages: Record<GuardProposal, string> = {
  sam: 'The output guard refused Sam, so nothing was written and no permission leaked.',
  long: `The output guard's schema check refused the ${LONG_TITLE.length}-character title.`,
  ravi: 'Every guard passed, so the task was written with Ravi as assignee.',
  'task-999': 'The output guard found no task 999, so nothing was written.',
};
export const GUARD_REFINEMENT = 'Refinement: guards on both sides of every model step.';
export function guardSummary(state: GuardState): Summary {
  if (!state.run) return { tone: 'neutral', message: GUARD_START_MESSAGE };
  return {
    tone: state.run.written ? 'success' : 'neutral',
    message: guardMessages[state.proposal],
  };
}
export const stoppedAtLabel = (run: GuardRun) =>
  run.stoppedAt === null ? 'nowhere' : (run.stages[run.stoppedAt]?.stage ?? '');

// --- decide-shape: the simplest shape that works ---------------------------------------------

export const REQUIREMENTS = ['classify', 'classify-extract', 'lookups', 'chain'] as const;
export type Requirement = (typeof REQUIREMENTS)[number];
export const requirementLabels: Record<Requirement, string> = {
  classify: 'Classify a message',
  'classify-extract': 'Classify, then extract',
  lookups: 'A question needing one to four lookups',
  chain: 'A fixed three-step chain',
};
export const toRequirement = (value: string): Requirement =>
  REQUIREMENTS.find((requirement) => requirement === value) ?? 'classify';
export interface Shape {
  shape: string;
  calls: string;
  code: string;
  counterexample: string;
}
export const shapes: Record<Requirement, Shape> = {
  classify: {
    shape: 'single call',
    calls: '1',
    code: 'one prompt and a schema',
    counterexample: 'A loop for this wastes calls on a one-call job.',
  },
  'classify-extract': {
    shape: 'routed workflow',
    calls: '2',
    code: 'a switch on the label',
    counterexample: 'One call for both jobs does each of them badly.',
  },
  lookups: {
    shape: 'loop',
    calls: '2 to 5',
    code: 'a stop condition and a cap on rounds',
    counterexample: 'A fixed chain fails when the question needs a fourth lookup.',
  },
  chain: {
    shape: 'chain',
    calls: '3',
    code: 'a fixed sequence with a log row per step',
    counterexample: 'A loop would spend calls choosing steps the author already knows.',
  },
};
export interface ShapeState {
  requirement: Requirement;
  chosen: boolean;
}
export const initialShape = (): ShapeState => ({ requirement: 'classify', chosen: false });
export type ShapeAction =
  { type: 'requirement'; value: Requirement } | { type: 'choose' } | { type: 'reset' };
export function shapeTransition(state: ShapeState, action: ShapeAction): ShapeState {
  switch (action.type) {
    case 'reset':
      return initialShape();
    case 'requirement':
      return { requirement: action.value, chosen: false };
    case 'choose':
      return { ...state, chosen: true };
  }
}
export const SHAPE_START_MESSAGE = 'Choose a requirement, then press Choose.';
export const SHAPE_REFINEMENT = 'Refinement: the simplest shape that works.';
export function shapeSummary(state: ShapeState): Summary {
  if (!state.chosen) return { tone: 'neutral', message: SHAPE_START_MESSAGE };
  return { tone: 'neutral', message: shapes[state.requirement].counterexample };
}

// --- decide-test: a test per rule ------------------------------------------------------------

export const CHANGES = ['none', 'remove-guard', 'add-label', 'shorten-title'] as const;
export type Change = (typeof CHANGES)[number];
export const changeLabels: Record<Change, string> = {
  none: 'None',
  'remove-guard': 'Remove the membership guard',
  'add-label': 'Add a label',
  'shorten-title': 'Shorten the title limit to 40',
};
export const toChange = (value: string): Change =>
  CHANGES.find((change) => change === value) ?? 'none';
export const TESTS = [
  'the guard refuses Sam',
  'the router maps note',
  'the router maps command',
  'compare drops the duplicate',
  'a long title is refused',
  'an unknown label asks',
] as const;
export type TestName = (typeof TESTS)[number];
export interface TestResult {
  name: TestName;
  passed: boolean;
  why: string;
}
/** The one test each change breaks, with the reason the fake client reports. */
export const breaks: Partial<Record<Change, [TestName, string]>> = {
  'remove-guard': ['the guard refuses Sam', 'The membership guard is gone, so Sam is accepted.'],
  'add-label': ['an unknown label asks', 'The new label has no code path.'],
};
export function runTests(change: Change): TestResult[] {
  const [brokenName, reason] = breaks[change] ?? [null, 'ok'];
  return TESTS.map((name) => ({
    name,
    passed: name !== brokenName,
    why: name === brokenName ? reason : 'ok',
  }));
}
export const SHORT_LIMIT = 40;
/** Titles from the recorded runs that a 40-character limit would refuse. */
export const NOW_REFUSED: readonly string[] = [
  'Order ten more name badges for the partners',
  'Check if the projector in the small room is broken',
];
export const nowRefusedLines = () =>
  NOW_REFUSED.map((title) => `${title} (${title.length} characters)`).join('\n');
export interface TestState {
  change: Change;
  results: TestResult[] | null;
}
export const initialTest = (): TestState => ({ change: 'none', results: null });
export type TestAction = { type: 'change'; value: Change } | { type: 'run' } | { type: 'reset' };
export function testTransition(state: TestState, action: TestAction): TestState {
  switch (action.type) {
    case 'reset':
      return initialTest();
    case 'change':
      return { change: action.value, results: null };
    case 'run':
      return { ...state, results: runTests(state.change) };
  }
}
export const passedCount = (results: readonly TestResult[]) =>
  results.filter((result) => result.passed).length;
export const TEST_START_MESSAGE = 'Choose a change, then press Run the tests.';
export const TEST_ALL_PASS_MESSAGE = '6 of 6 pass, so every rule still holds.';
export const TEST_GUARD_MESSAGE = 'One guard test fails and every model test stays green.';
export const TEST_LABEL_MESSAGE = 'The new label has no code path, so one test fails.';
export const TEST_SHORTEN_MESSAGE =
  '6 of 6 pass, but two of the proposals above would now be refused.';
export const TEST_REFINEMENT = 'Refinement: a test per rule.';
export function testSummary(state: TestState): Summary {
  if (!state.results) return { tone: 'neutral', message: TEST_START_MESSAGE };
  switch (state.change) {
    case 'none':
      return { tone: 'success', message: TEST_ALL_PASS_MESSAGE };
    case 'remove-guard':
      return { tone: 'warning', message: TEST_GUARD_MESSAGE };
    case 'add-label':
      return { tone: 'warning', message: TEST_LABEL_MESSAGE };
    case 'shorten-title':
      return { tone: 'warning', message: TEST_SHORTEN_MESSAGE };
  }
}
